import { Component, signal, inject, computed, effect, untracked, ViewChild, DestroyRef, OnDestroy, ChangeDetectionStrategy, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FormsModule } from '@angular/forms';

import { map, distinctUntilChanged, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { Drawer } from '@openng/optimus-ui/drawer';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { MenuItem, MessageService } from '@openng/optimus-ui/api';
import { MenuModule } from '@openng/optimus-ui/menu';
import { Select } from '@openng/optimus-ui/select';
import { ChipModule } from '@openng/optimus-ui/chip';

import { SearchComponent } from 'src/app/components/search/search.component';
import { DashboardDateRange } from 'src/app/models/dashboard-date-range.model';
import { TextSearchComponent } from 'src/app/components/text-search/text-search.component';
import { OpenSearchCriteria } from 'src/app/models/open-search-criteria.model';
import { ManualImportComponent } from 'src/app/components/manual-import/manual-import.component';
import { filterPropertyKeysForLevel } from 'src/app/models/property-filter-config';
import { PropertyFilterPanelComponent } from 'src/app/components/property-filter-panel/property-filter-panel.component';
import { DtcFilterComponent } from 'src/app/components/dtc-filter/dtc-filter.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { TerminologyService } from 'src/app/services/terminology.service';
import { isTreoEnvironment as isTreoEnv, isResultsEnvironment as isResultsEnv } from 'src/app/utils/environment.utils';
import { SearchStateService } from 'src/app/services/search-state.service';
import { OpenSearchService } from 'src/app/services/api/open-search.service';
import { ResultsDataAdapterService } from 'src/app/services/results-data-adapter.service';
import { DashboardDataService } from 'src/app/services/dashboard-data.service';
import { environment } from 'src/environments/environment';
import { GroupFilterComponent } from 'src/app/components/group-filter/group-filter.component';

/**
 * Sidebar component with search and import functionality
 */
@Component({
	selector: 'app-sidebar',
	imports: [
		AsyncPipe,
		Drawer,
		ButtonModule,
		TooltipModule,
		SearchComponent,
		ManualImportComponent,
		MenuModule,
		Select,
		FormsModule,
		ChipModule,
		PropertyFilterPanelComponent,
		DtcFilterComponent,
		GroupFilterComponent,
		TextSearchComponent,
	],
	templateUrl: './sidebar.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnDestroy {
	private router = inject(Router);

	private destroyRef = inject(DestroyRef);

	private environmentService = inject(EnvironmentService);

	private terminologyService = inject(TerminologyService);

	private breakpointObserver = inject(BreakpointObserver);

	readonly navigationItems = input<MenuItem[]>([]);

	// Wraps each item's command so selecting it also closes the mobile sidebar.
	navigationMenuItems = computed<MenuItem[]>(() =>
		this.navigationItems().map(item => ({
			...item,
			command: (event: any) => {
				item.command?.(event);
				this.sidebarVisible.set(false);
			},
		})),
	);

	readonly environments = input<
		{
			label: string;
			value: string;
		}[]
	>([]);

	public get selectedEnvironment() {
		const current = this.environmentService.getCurrentEnvironment();
		return this.environments().find(e => e.value === current) ?? null;
	}

	public set selectedEnvironment(value: { label: string; value: string } | null) {
		if (value) {
			this.environmentService.setEnvironment(value.value);
			this.isMobile$.pipe(take(1)).subscribe(isMobile => {
				if (isMobile) this.sidebarVisible.set(false);
			});
		}
	}

	public breakpoint = `${environment.breakPoint.toString()}px`;

	public isMobile$ = this.breakpointObserver.observe([`(max-width: ${this.breakpoint})`]).pipe(
		map(result => result.matches),
		distinctUntilChanged(),
	);

	private searchStateService = inject(SearchStateService);

	private openSearchService = inject(OpenSearchService);

	private resultsDataAdapter = inject(ResultsDataAdapterService);

	private dashboardDataService = inject(DashboardDataService);

	private messageService = inject(MessageService);

	// ViewChild references to access panel components
	@ViewChild(ManualImportComponent) manualImportComponent?: ManualImportComponent;

	@ViewChild('searchComponent') searchComponent?: SearchComponent;

	// Make sidebarVisible readonly so it can be accessed from parent components
	readonly sidebarVisible = signal<boolean>(true);

	// Time selection toggle
	showTimeSelection = signal<boolean>(false);

	// Expose service filter state to template
	public get availablePropertyKeys() {
		return this.dashboardDataService.availablePropertyKeys;
	}

	public get availablePropertyKeysByLevel() {
		return this.dashboardDataService.availablePropertyKeysByLevel;
	}

	// The DTC pair key is fetched like any ms property, but rendered by the dedicated cascading
	// app-dtc-filter (ECU→code) instead of a plain dropdown — so it's excluded from the generic ms panel
	// keys below.
	private static readonly DTC_KEYS = ['dtc_pair'];

	// Property-filter keys actually shown in the GENERIC panels, after applying the per-environment
	// filter config (property-filter-config.ts). Environments with no config show all keys.
	// This restricts the filter OPTIONS only; the underlying property data is unaffected. The DTC key
	// is stripped from ms because the custom DTC filter renders it.
	visiblePropertyKeysByLevel = computed<{ ms: string[]; ss: string[]; tc: string[] }>(() => {
		const env = this.currentEnvironment();
		const all = this.dashboardDataService.availablePropertyKeysByLevel();
		const ms = filterPropertyKeysForLevel(env, 'ms', all.ms).filter(k => !SidebarComponent.DTC_KEYS.includes(k.toLowerCase()));
		return {
			ms,
			ss: filterPropertyKeysForLevel(env, 'ss', all.ss),
			tc: filterPropertyKeysForLevel(env, 'tc', all.tc),
		};
	});

	// The DTC "ECU:code" pairs, read from the server-fetched property data (same source the generic panels
	// use). This single list feeds BOTH dropdowns — the ECU dropdown is derived from the pair prefixes.
	// Empty when the environment/data has no DTC facet, which hides the control.
	readonly dtcPairOptions = computed<string[]>(() => this.dashboardDataService.getPropertyValuesByLevel('dtc_pair', 'ms').map(o => o.value));

	// The pending dtc_pair selection ("ECU:code"), read from the pending filters under the namespaced key.
	readonly pendingDtcPair = computed<string | null>(() => this.pendingFilters()['ms:dtc_pair'] ?? null);

	// True when the DTC filter has any pairs to show (so the sidebar can decide whether to render it).
	readonly hasDtcFilter = computed<boolean>(() => this.dtcPairOptions().length > 0);

	public get propertyFilters() {
		return this.dashboardDataService.propertyFilters;
	}

	// PENDING sidebar selections, not yet applied. The panels bind to this (not the service's active
	// filters) so picking a value does NOT fetch — the user can set several filters freely and then click
	// "Apply" to commit them in one server load. Seeded from the active filters and resynced whenever those
	// change externally (e.g. a date-range/environment load clears them), via syncPendingToActive below.
	readonly pendingFilters = signal<Record<string, string | null>>({ ...this.dashboardDataService.propertyFilters() });

	// Snapshot of the applied filters as of the last sync, so syncPendingToActive can tell whether the user
	// has UNAPPLIED pending edits (pending diverged from the applied set) before deciding to overwrite them.
	private lastSyncedActive: Record<string, string | null> = { ...this.dashboardDataService.propertyFilters() };

	// Order-independent signature of a filter map's non-null entries, for comparing two filter sets.
	private filterSignature(f: Record<string, string | null>): string {
		return Object.entries(f)
			.filter(([, v]) => v != null)
			.map(([k, v]) => `${k}=${v}`)
			.sort()
			.join('|');
	}

	// Keep pending in sync when the applied filters change from OUTSIDE the sidebar (a new date range or
	// environment clears them; Apply/Clear commit them; another view may set one). The guard protects the
	// user's in-progress, not-yet-applied pending edits: we only overwrite pending when it has NO unapplied
	// changes relative to the previously-applied set, OR when the applied set was hard-reset to empty (a new
	// range/environment load — which must win). Otherwise the user's unapplied selection is preserved.
	private syncPendingToActive = effect(() => {
		const active = this.dashboardDataService.propertyFilters();
		untracked(() => {
			const activeIsEmpty = !Object.values(active).some(v => v != null);
			const pendingHasUnappliedEdits = this.filterSignature(this.pendingFilters()) !== this.filterSignature(this.lastSyncedActive);
			if (!pendingHasUnappliedEdits || activeIsEmpty) {
				this.pendingFilters.set({ ...active });
			}
			this.lastSyncedActive = { ...active };
		});
	});

	// Refresh the server-cascaded dropdown options whenever the pending selection changes (user pick, clear,
	// or external resync) — one cheap size:0 terms-agg call per key, no heavy data load (that waits for
	// Apply). Server-side for ALL environments and levels, so cross-level cascade (ms↔ss↔tc) stays correct.
	// Reads only pendingFilters as its dependency; the service call is made untracked.
	private previewOptionsOnPendingChange = effect(() => {
		const pending = this.pendingFilters();
		untracked(() => this.dashboardDataService.previewPropertyFilterOptions(pending));
	});

	// Expanded state of each property-filter panel, OWNED HERE (not in the panel) so it survives the panel
	// being recreated when its options briefly empty during an Apply refetch — keeping the user's filters
	// visible instead of collapsing. Two-way bound to each app-property-filter-panel's `expanded` model.
	readonly msExpanded = signal<boolean>(false);

	readonly ssExpanded = signal<boolean>(false);

	readonly tcExpanded = signal<boolean>(false);

	// Expanded state of the DTC filter panel (its own collapsible section with a header, like the property
	// panels). Collapsed by default so the sidebar stays compact until the user opens it.
	readonly dtcExpanded = signal<boolean>(false);

	// True when pending selections differ from the applied filters — enables the "Apply" button.
	hasPendingChanges = computed(() => this.filterSignature(this.pendingFilters()) !== this.filterSignature(this.dashboardDataService.propertyFilters()));

	// True when a big date range was selected but not yet loaded (only sidebar options fetched). The user
	// must press Apply to run the load — INCLUDING with no filters.
	readonly deferredUnloaded = this.dashboardDataService.deferredUnloaded;

	// The single Apply button shows when there are pending filter changes OR a deferred range is waiting to
	// load — so one button covers both "apply my filters" and "just load this range" (no filters needed).
	readonly showApply = computed(() => this.hasPendingChanges() || this.deferredUnloaded());

	// True when any pending filter has a value — drives the "Clear all filters" button visibility.
	hasPendingFilter = computed(() => Object.values(this.pendingFilters()).some(v => v != null));

	public get hasActivePropertyFilter() {
		return this.dashboardDataService.hasActivePropertyFilter;
	}

	// True during the heavy Apply refetch (the paged summaries load), but NOT during the cheap per-pick
	// option preview — so dimming on this signals "applying your filters" without flickering on every pick.
	// Used to dim + lock the filter panels so the user sees an "updating" state instead of silently stale
	// dropdowns during an Apply refetch.
	public dashboardLoading$ = this.dashboardDataService.loading$;

	public getPropertyValues(key: string) {
		return this.dashboardDataService.getPropertyValues(key);
	}

	public getPropertyValuesByLevel(key: string, level: 'ms' | 'ss' | 'tc') {
		return this.dashboardDataService.getPropertyValuesByLevel(key, level);
	}

	// All option lists come from the server-fetched, server-cascaded propertyFilterData, refreshed per pick
	// via previewPropertyFilterOptions. One uniform path for every level and environment, so cross-level
	// cascade is always correct (an ss/tc selection narrows the ms dropdowns and vice versa).
	getMsOptions = (key: string) => this.getPropertyValuesByLevel(key, 'ms');

	getSsOptions = (key: string) => this.getPropertyValuesByLevel(key, 'ss');

	getTcOptions = (key: string) => this.getPropertyValuesByLevel(key, 'tc');

	public hasAnyVisibleFilters(): boolean {
		const levels = this.visiblePropertyKeysByLevel();
		return (
			this.hasDtcFilter() || levels.ms.some(k => this.getMsOptions(k).length > 0) || levels.ss.some(k => this.getSsOptions(k).length > 0) || levels.tc.some(k => this.getTcOptions(k).length > 0)
		);
	}

	// Records a filter selection in PENDING state only — the heavy data load waits for Apply. Cascading the
	// OTHER dropdowns is handled reactively: TREO narrows ms options CLIENT-SIDE (getMsOptions reads
	// pendingFilters, no fetch); non-TREO environments refresh server options via previewOptionsOnPendingChange.
	public setPropertyFilter(key: string, value: string | null) {
		this.pendingFilters.update(f => ({ ...f, [key]: value }));
	}

	// Records the DTC pairing selection ("ECU:code", or null to clear) as the pending ms:dtc_pair filter.
	// Routes through the same pending-filter flow as the generic panels, so it commits on Apply with
	// everything else and shows in the Apply/Clear affordances.
	public setDtcPair(pair: string | null) {
		this.setPropertyFilter('ms:dtc_pair', pair);
	}

	// Commits the pending selections to the service in one batch, firing a single server refetch.
	public applyPendingFilters() {
		this.dashboardDataService.setPropertyFilters(this.pendingFilters());
	}

	// Clears pending selections and, if any filter is actually applied, commits the empty set (one fetch).
	public clearPropertyFilters() {
		this.pendingFilters.set({});
		if (this.dashboardDataService.hasActivePropertyFilter()) {
			this.dashboardDataService.clearPropertyFilters();
		}
	}

	public activeFilterCount(): number {
		return Object.values(this.dashboardDataService.propertyFilters()).filter(v => v != null).length;
	}

	// Check if there's a previous search
	hasLastSearch = computed(() => this.searchStateService.hasLastSearch());

	// Environment availability - used in ManualImportComponent and SearchComponent
	hasEnvironment = signal<boolean>(!!this.environmentService.getCurrentEnvironment());

	// Current analyzer environment, kept reactive so the per-environment property-filter config
	// (visiblePropertyKeysByLevel) recomputes when the environment changes.
	currentEnvironment = signal<string | null>(this.environmentService.getCurrentEnvironment() ?? null);

	isTreoEnvironment = signal<boolean>(isTreoEnv(this.environmentService.getCurrentEnvironment()));

	isResultsEnvironment = signal<boolean>(isResultsEnv(this.environmentService.getCurrentEnvironment()));

	isEmuEnvironment = signal<boolean>(this.environmentService.getCurrentEnvironment()?.toLowerCase().includes('emu') || false);

	// Property-filter panel titles for the current environment (react to currentEnvironment).
	mainSuitePropertiesTitle = computed(() => `${this.terminologyService.term('mainSuiteProp', this.currentEnvironment())} Properties`);

	subSuitePropertiesTitle = computed(() => `${this.terminologyService.term('subSuiteProp', this.currentEnvironment())} Properties`);

	testCasePropertiesTitle = computed(() => `${this.terminologyService.term('testCaseProp', this.currentEnvironment())} Properties`);

	/**
	 * Emits dashboard data fetched by date range - parent component (dashboard) subscribes to this
	 */
	readonly dashboardData = output<any>();

	// The in-flight dashboard export (paged over multiple pages). Held so a new fetch — e.g. from a
	// rapid date-range/environment change, or the same trigger firing twice — cancels the previous one
	// instead of running two overlapping exports (which would stack two "loading more" toasts).
	private dashboardFetchSubscription?: Subscription;

	private lastDateRange: DashboardDateRange | null = null;

	private previousEnvironment: string | null = null;

	constructor() {
		this.environmentService.environment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(env => {
			this.hasEnvironment.set(!!env);
			this.currentEnvironment.set(env ?? null);
			this.isTreoEnvironment.set(isTreoEnv(env));
			this.isResultsEnvironment.set(isResultsEnv(env));
			this.isEmuEnvironment.set(env?.toLowerCase().includes('emu') || false);

			// Navigate to dashboard when environment changes on certain pages
			if (this.previousEnvironment !== null && env !== this.previousEnvironment) {
				const url = this.router.url.split('?')[0];
				if (url.includes('suite-details') || url.includes('search-results')) {
					void this.router.navigate(['/']);
				}
			}
			this.previousEnvironment = env;

			if (env && this.lastDateRange?.startDate && this.lastDateRange?.endDate) {
				this.fetchDashboardData(this.lastDateRange);
			}
		});
	}

	ngOnDestroy(): void {
		// Held explicitly (not via takeUntilDestroyed) because it's also cancelled mid-flight on a new fetch.
		this.dashboardFetchSubscription?.unsubscribe();
	}

	public toggleSidebar() {
		this.sidebarVisible.update(value => !value);
	}

	/**
	 * Opens the sidebar and expands the specific section panel
	 * @param section
	 */
	public openSidebarToSection(section: 'import' | 'filter' | 'search') {
		this.sidebarVisible.set(true);

		// Wait for the view to update, then expand the appropriate panel. Drives the sidebar-owned expanded
		// signals (two-way bound to each panel) so the state sticks even across a panel recreation.
		setTimeout(() => {
			const setAllFilters = (expanded: boolean) => {
				this.msExpanded.set(expanded);
				this.ssExpanded.set(expanded);
				this.tcExpanded.set(expanded);
				this.dtcExpanded.set(expanded);
			};
			const collapseAllFilters = () => setAllFilters(false);
			const expandAllFilters = () => setAllFilters(true);
			switch (section) {
				case 'import':
					if (this.manualImportComponent && !this.manualImportComponent.panelExpanded()) {
						this.manualImportComponent.togglePanel();
					}
					collapseAllFilters();
					break;
				case 'filter':
					expandAllFilters();
					if (this.manualImportComponent?.panelExpanded()) {
						this.manualImportComponent.togglePanel();
					}
					break;
				case 'search':
					if (this.manualImportComponent?.panelExpanded()) {
						this.manualImportComponent.togglePanel();
					}
					collapseAllFilters();
					break;
			}
		}, 100);
	}

	/**
	 * Converts a picker Date to an ABSOLUTE (frozen) date-range bound string for the OpenSearch query.
	 * Frozen bounds are required because the dashboard pages the result with a composite cursor: a
	 * `now`-relative bound would be re-resolved by OpenSearch on every page, sliding the window forward
	 * between pages and invalidating the page-1 cursor. An absolute bound gives every page the identical
	 * window.
	 *
	 * localWallClock=true (TREO, which queries the naive-local `startTime` field): emit local wall-clock
	 * "YYYY-MM-DDTHH:mm:ss" with NO zone, so it compares like-for-like against the stored digits.
	 * localWallClock=false (other envs, which query true-UTC `createdAt`): emit the UTC ISO string.
	 * @param date
	 * @param localWallClock
	 */
	private toAbsoluteBound(date: Date | null, localWallClock: boolean): string | null {
		if (!date) return null;
		if (!localWallClock) {
			return date.toISOString();
		}
		const p = (n: number) => `${n}`.padStart(2, '0');
		return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}` + `T${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
	}

	/**
	 * Handles dashboard date range changes - fetches dashboard data
	 * @param dateRange
	 */
	public onDateRangeChange(dateRange: DashboardDateRange) {
		this.lastDateRange = dateRange;
		this.fetchDashboardData(dateRange);
	}

	/**
	 * Fetches dashboard data for the given date range.
	 * Routes to Results API adapter for Results environment, OpenSearch for all others.
	 * @param dateRange
	 * @param forceLoad
	 */
	private fetchDashboardData(dateRange: DashboardDateRange, forceLoad = false) {
		const currentEnv = this.environmentService.getCurrentEnvironment();
		// Validate that an environment is selected
		if (!currentEnv || currentEnv.trim() === '') {
			return;
		}

		// Validate that both dates are present
		if (!dateRange.startDate || !dateRange.endDate) {
			return;
		}

		// Set loading state before fetching
		this.dashboardDataService.setLoading();

		if (isResultsEnv(currentEnv)) {
			// Format dates
			const startDate = dateRange.startDate.toISOString();
			const endDate = dateRange.endDate.toISOString();

			this.resultsDataAdapter
				.getDocumentsByDateRange$(startDate, endDate)
				.pipe(take(1))
				.subscribe({
					next: results => {
						// Non-paged source: the full raw set is pushed directly, so consumers should read it (not the
						// server-filtered endpoints). Mark paged mode off before publishing.
						this.dashboardDataService.pagedMode.set(false);
						this.dashboardDataService.deferredUnloaded.set(false);
						// Update dashboard data service for dashboard component to consume
						// Pass the original Date objects from the date picker
						this.dashboardDataService.setDashboardData(results, dateRange.startDate, dateRange.endDate);
						// Also emit for any parent components that might be listening
						this.dashboardData.emit(results);
					},
					error: (error: any) => {
						console.error('[SidebarComponent] Error fetching Results data:', error);
						// Reset loading state on error
						this.dashboardDataService.pagedMode.set(false);
						this.dashboardDataService.setDashboardData([], null, null);
					},
				});
		} else {
			// Freeze the date-range bounds to ABSOLUTE timestamps once, here — NOT relative `now-…` math.
			// The dashboard pages the result (300 suites/page) with a composite cursor; if the bounds stayed
			// `now`-relative, OpenSearch would re-resolve `now` on every page and the window would slide
			// forward between pages, invalidating the page-1 cursor and breaking multi-page loads. Absolute
			// bounds give every page the identical window, so the cursor stays valid.
			//
			// TREO queries on `startTime`, a naive LOCAL wall-clock value (no zone), so send local wall-clock
			// strings with no 'Z' to compare like-for-like. Other environments query on `createdAt` (true
			// UTC), so send UTC ISO.
			const useLocalWallClock = isTreoEnv(currentEnv);
			const startDate = this.toAbsoluteBound(dateRange.startDate, useLocalWallClock);
			const endDate = this.toAbsoluteBound(dateRange.endDate, useLocalWallClock);

			// Store the fetch context on the service so the paged auto-loader requests each subsequent page
			// with the same environment + FROZEN bounds. Starting a new load supersedes any in-progress one
			// (generation token in the service), so switching date range or environment cancels the previous
			// page chain instead of mixing its pages into the new data.
			//
			// Quick-filter PRESETS (1 Day / 1 Week / 2 Weeks / 1 Month / 3 Months) always load IMMEDIATELY —
			// they're one-tap "show me this" actions. Only a MANUAL (date-picker) range longer than two weeks
			// DEFERS: fetch just the cheap range-scoped options and wait for Apply, since a big custom range
			// auto-load is slow and usually wasted when the user wants a filtered slice (or can Apply with no
			// filters to load it all). Deferral is limited to envs that HAVE the filter section, so
			// the one Apply button is always reachable.
			const defer = !forceLoad && dateRange.preset === null && !this.isEmuEnvironment() && this.isBigRange(dateRange.startDate, dateRange.endDate);
			if (defer) {
				this.dashboardDataService.loadOptionsOnly(currentEnv, startDate, endDate, dateRange.startDate, dateRange.endDate);
			} else {
				this.dashboardDataService.startPagedLoad(currentEnv, startDate, endDate, dateRange.startDate, dateRange.endDate);
			}
		}
	}

	// Max INCLUSIVE calendar days a range can span and still auto-load, keyed to the LARGEST quick-filter
	// each environment offers (so "up to the biggest preset" always loads immediately, preset or manual):
	//   - TREO shows up to "2 Weeks" (its month buttons are hidden) -> 15 inclusive days.
	//   - Other envs show up to "3 Months" -> 95 days, a generous cover for any 3-calendar-month span.
	// Anything longer defers to Apply.
	private static readonly MAX_AUTOLOAD_DAYS_TREO = 15;

	private static readonly MAX_AUTOLOAD_DAYS_DEFAULT = 95;

	/**
	 * True when the selected range spans more inclusive calendar days than this environment's largest
	 * quick-filter, so the heavy load should be deferred to Apply.
	 * @param start
	 * @param end
	 */
	private isBigRange(start: Date, end: Date): boolean {
		const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
		const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
		const inclusiveDays = Math.round((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24)) + 1;
		const maxDays = this.isTreoEnvironment() ? SidebarComponent.MAX_AUTOLOAD_DAYS_TREO : SidebarComponent.MAX_AUTOLOAD_DAYS_DEFAULT;
		return inclusiveDays > maxDays;
	}

	/**
	 * Handles OpenSearch text search submission.
	 * Gets current environment from EnvironmentService and navigates to search results page.
	 * @param criteria
	 */
	public onTextSearch(criteria: OpenSearchCriteria) {
		this.isMobile$.pipe(take(1)).subscribe(isMobile => {
			if (isMobile) this.sidebarVisible.set(false);
		});

		// Get current environment from service
		const environment = this.environmentService.getCurrentEnvironment();

		// Validate that an environment is selected
		if (!environment || environment.trim() === '') {
			console.warn('[SidebarComponent] Cannot search without an environment selected');
			return;
		}

		// Validate search term
		if (!criteria.searchTerm || criteria.searchTerm.trim() === '') {
			console.warn('[SidebarComponent] Search term is empty');
			return;
		}

		const searchTerm = criteria.searchTerm.trim();

		// Save search state (without hasResults - will be set when results are viewed)
		this.searchStateService.setLastSearch({
			environment,
			searchTerm,
			hasResults: false,
		});

		// Navigate to search results with query params
		const queryParams: any = {
			environment,
			searchTerm,
		};

		// Note: Date range is NOT included in OpenSearch - completely independent

		void this.router.navigate(['/search-results'], { queryParams });
	}

	/**
	 * Navigates back to the last search results and closes sidebar on mobile.
	 */
	public goToLastSearch() {
		const lastSearch = this.searchStateService.getLastSearch();
		if (lastSearch) {
			this.isMobile$.pipe(take(1)).subscribe(isMobile => {
				if (isMobile) this.sidebarVisible.set(false);
			});

			void this.router.navigate(['/search-results'], {
				queryParams: {
					environment: lastSearch.environment,
					searchTerm: lastSearch.searchTerm,
				},
			});
		}
	}

	/**
	 * Refetches dashboard data for the currently selected date range without changing it.
	 * The date range normally auto-fetches only when it CHANGES (via the search effect), so a
	 * manual refresh re-runs the fetch with the same bounds. Falls back to reading the current
	 * dates off the search component if no range has been emitted yet.
	 */
	public refetchDashboardData() {
		// Reuse the last emitted range (which carries its preset/manual origin). Fall back to the picker's
		// current dates when nothing has been emitted yet — treated as a manual range (preset: null).
		const range: DashboardDateRange | null =
			this.lastDateRange ?? (this.searchComponent ? { startDate: this.searchComponent.startDate(), endDate: this.searchComponent.endDate(), preset: null } : null);
		if (range?.startDate && range?.endDate) {
			// Force the load: the reload button should behave like Apply and fire immediately, even for a
			// big range that would otherwise defer to the "press Apply" gate.
			this.fetchDashboardData(range, true);
		}
	}

	/**
	 * Toggles time selection mode in the search component
	 */
	public toggleTimeSelection() {
		const newValue = !this.showTimeSelection();
		this.showTimeSelection.set(newValue);

		// Delegate to search component
		if (this.searchComponent) {
			this.searchComponent.toggleTimeSelection();
		}
	}
}
